import { getAccessToken } from "@/lib/auth-storage";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";

export type RealtimeEvent = {
  type: string;
  topics?: string[];
  at?: string;
};

type ConnectRealtimeStreamOptions = {
  onEvent: (event: RealtimeEvent) => void;
  onError?: (error: unknown) => void;
};

function parseSseChunk(chunk: string) {
  const frames = chunk.split(/\n\n+/);
  return {
    complete: frames.slice(0, -1),
    remainder: frames.at(-1) ?? "",
  };
}

function parseSseEvent(frame: string): RealtimeEvent | null {
  const normalized = frame.replace(/\r/g, "");
  const lines = normalized.split("\n");
  const dataLines: string[] = [];

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    if (!line || line.startsWith(":")) continue;
    if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  if (!dataLines.length) return null;

  try {
    return JSON.parse(dataLines.join("\n")) as RealtimeEvent;
  } catch {
    return null;
  }
}

export function connectRealtimeStream(options: ConnectRealtimeStreamOptions) {
  let aborted = false;
  let reconnectTimer: number | null = null;
  let controller: AbortController | null = null;

  const scheduleReconnect = () => {
    if (aborted) return;
    reconnectTimer = window.setTimeout(() => {
      void open();
    }, 3000);
  };

  const open = async () => {
    const token = getAccessToken();
    if (!token || aborted) return;

    controller = new AbortController();

    try {
      const response = await fetch(`${API_BASE}/events/stream`, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`Realtime stream failed (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (!aborted) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const { complete, remainder } = parseSseChunk(buffer);
        buffer = remainder;

        for (const frame of complete) {
          const event = parseSseEvent(frame);
          if (event) {
            options.onEvent(event);
          }
        }
      }
    } catch (error) {
      if (!aborted) {
        options.onError?.(error);
      }
    } finally {
      if (!aborted) {
        scheduleReconnect();
      }
    }
  };

  void open();

  return () => {
    aborted = true;
    if (reconnectTimer !== null) {
      window.clearTimeout(reconnectTimer);
    }
    controller?.abort();
  };
}
